import { useEffect, useImperativeHandle, useMemo, useState, type Ref } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { useFuenteDeLaDescripcion } from "@/hooks/useFuenteDeLaDescripcion";
import { useRellenarMaquinariasConIa, useResolverElRelleno } from "@/hooks/useRellenoConIa";
import { getApiErrorMessage } from "@/lib/api";
import { formatearMonto } from "@/lib/montos";
import { cn } from "@/lib/utils";
import type { MaquinaPropuesta } from "@/lib/rellenoConIa";
import type {
  CamposDeMaquinaria,
  GuardarMaquinariaInput,
  Informe,
  MaquinariaDeInforme,
} from "@/lib/informes";

import { BandaDeRellenoConIa } from "./BandaDeRellenoConIa";
import { BarraDeGuardado } from "./BarraDeGuardado";
import { DescripcionDeLaMaquinaria } from "./DescripcionDeLaMaquinaria";
import { DialogoDeMaquina, type GuardadoDeLaMaquina } from "./DialogoDeMaquina";
import { FilasPropuestas } from "./FilasPropuestas";
import { FuenteDeLaPropuesta } from "./FuenteDeLaPropuesta";
import { ListaDeMaquinarias } from "./ListaDeMaquinarias";
import { NotasDeLaPestana } from "./NotasDeLaPestana";
import {
  AvisoDeLoRellenado,
  type AvisoDelRelleno,
} from "./RellenoConIa";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import {
  formularioDesdeLaPropuesta,
  resumenDeLoPropuestoEnMaquinarias,
  yaEstaEnElCuadro,
} from "./maquinariasConIa";
import { camposDesde } from "./maquinaria";
import {
  campoDeLaDescripcionDeMaquinarias,
  itemsPorEnlazarDeMaquinarias,
  seccionDeMaquinarias,
  tituloDelAviso,
  type TerrenoDeLaLista,
} from "./maquinariasTerreno";
import { modoDelTerreno } from "./cuadroDeConstrucciones";
import { VisorDeFotos } from "./FotosDeTerreno";
import { TerrenoDeLaDescripcion } from "./TerrenoDeLaDescripcion";
import { fotosDelVisor } from "./registroDeTerreno";
import type { EstadoDelTerreno } from "./terrenoDeAntecedentes";

/** Lo que la pantalla puede pedirle a la pestaña desde el aviso de salida. */
export interface PestanaDeMaquinariasHandle {
  /** Guarda, igual que apretar el botón de la pestaña. */
  guardar: () => void;
  /** Toma lo que hay en pantalla como "lo guardado" y apaga el aviso. */
  marcarGuardado: () => void;
}

interface PestanaDeMaquinariasProps {
  informe: Informe;
  /**
   * Lo que el encargado sincronizó desde la App de terreno. Solo se LEE: las
   * máquinas que creó llegan como filas TERRENO y lo registrado en cada fila se
   * ve bajo ella. Sin él (módulo apagado) la pestaña se dibuja sin ninguna
   * pieza de terreno.
   */
  terreno?: EstadoDelTerreno;
  /** Quién tiene asignada la visita: da el nombre en «aún sin sincronizar». */
  asignado?: string | null;
  /** El "Guardar cambios" de la pestaña está en vuelo. */
  guardando: boolean;
  /** Hay una operación de fila en vuelo (alta, corrección, borrado, foto). */
  ocupada: boolean;
  ref?: Ref<PestanaDeMaquinariasHandle>;
  /** Avisa si hay cambios sin guardar en esta pestaña. */
  onCambios?: (hayCambios: boolean) => void;
  /** Crea la máquina y, con el id que devuelve, sube sus fotos. */
  onAgregar: (campos: CamposDeMaquinaria, fotos: File[]) => void;
  onCorregir: (maquinariaId: number, campos: CamposDeMaquinaria) => void;
  onQuitar: (maquinariaId: number) => void;
  /** «Mantener» en el aviso de terreno «No está en el predio». */
  onMantener?: (maquinariaId: number) => void;
  onSubirFoto: (maquinariaId: number, archivo: File) => void;
  onQuitarFoto: (maquinariaId: number, fotoId: number) => void;
  /** Guarda la leyenda libre de una foto de la fila (en el acto, fuera del formulario). */
  onLeyendaDeFoto?: (maquinariaId: number, fotoId: number, leyenda: string | null) => void;
  onGuardar: (input: GuardarMaquinariaInput) => void;
}

/**
 * La pestaña "Maquinarias y equipos": la lista de máquinas del predio, su
 * descripción general y la casilla que declara que no hay ninguna.
 *
 * DOS BOTONES DE GUARDAR, Y NO ES UNA INCONSISTENCIA. "Guardar máquina" crea o
 * corrige UNA fila y se va al servidor en el acto; "Guardar cambios" escribe lo
 * que es de la PESTAÑA —la descripción general, su casilla, la casilla "no
 * cuenta con maquinaria" y las notas—. La división es la misma que ya tiene la
 * pestaña de descripción entre su texto y sus imágenes, y por el mismo motivo:
 * una máquina tiene fotos colgando, así que si todo viajara junto, agregar una
 * obligaría a reenviar el texto de la pantalla y corregir una coma obligaría a
 * reenviar la tabla entera.
 *
 * POR ESO LAS MÁQUINAS NO CUENTAN COMO "CAMBIOS SIN GUARDAR": cuando ese aviso
 * aparece, ya están en el servidor.
 *
 * LA CASILLA "Validar" VA POR FILA, igual que la de los deslindes: con doce
 * máquinas, una casilla al pie no diría cuál se revisó — y eso es exactamente
 * lo que existe para garantizar, porque estas filas las va a proponer la IA.
 * Se guarda SOLA, en el acto: la fila ya existe, así que marcarla es un acto
 * sobre algo guardado y no un borrador.
 *
 * LAS FOTOS SE ADMINISTRAN EN EL FORMULARIO y la tabla solo las cuenta. Seis
 * miniaturas dentro de una celda no se ven ni se pueden quitar; el formulario
 * ya es el lugar donde esa máquina se mira entera.
 */
export function PestanaDeMaquinarias({
  informe,
  terreno,
  asignado = null,
  guardando,
  ocupada,
  ref,
  onCambios,
  onAgregar,
  onCorregir,
  onQuitar,
  onMantener,
  onSubirFoto,
  onQuitarFoto,
  onLeyendaDeFoto,
  onGuardar,
}: PestanaDeMaquinariasProps) {
  const maquinas = useMemo(() => informe.maquinarias ?? [], [informe.maquinarias]);
  const estado = informe.secciones?.maquinaria;

  /* --- Lo de terreno --- */

  const registroDeTerreno = terreno?.estado === "ok" ? terreno.registro : undefined;
  const seccionDeTerreno = seccionDeMaquinarias(registroDeTerreno);
  const porEnlazar = useMemo(
    () => itemsPorEnlazarDeMaquinarias(registroDeTerreno, maquinas),
    [registroDeTerreno, maquinas],
  );
  const fotosDelRegistro = useMemo(
    () => (seccionDeTerreno ? fotosDelVisor([seccionDeTerreno], "todo") : []),
    [seccionDeTerreno],
  );
  const [fotoAbierta, setFotoAbierta] = useState<string | null>(null);

  const [noAplica, setNoAplica] = useState(estado?.no_aplica ?? false);
  const [descripcion, setDescripcion] = useState(estado?.descripcion_general ?? "");
  const fuenteDelTexto = useFuenteDeLaDescripcion(descripcion);
  const [validada, setValidada] = useState(estado?.descripcion_general_validada ?? false);
  const [notas, setNotas] = useState(informe.notas?.maquinaria ?? "");

  /* La referencia de "lo guardado" es estado y no las props: después de un
     guardado exitoso la base tiene que moverse a lo que quedó en pantalla, y
     las props recién traen ese dato cuando vuelve el refetch. */
  const [base, setBase] = useState({
    noAplica: estado?.no_aplica ?? false,
    descripcion: estado?.descripcion_general ?? "",
    validada: estado?.descripcion_general_validada ?? false,
    notas: informe.notas?.maquinaria ?? "",
  });

  /**
   * Las máquinas que la IA propuso y todavía nadie guardó.
   *
   * VIVEN ACÁ Y NO EN EL CUADRO, que es toda la diferencia con aguas y suelos:
   * la lista de máquinas guardadas sale de la caché del informe, así que meter
   * la propuesta ahí las haría aparecer como si estuvieran guardadas y el
   * siguiente refresco las borraría.
   *
   * SE DECLARAN ARRIBA DE `hayCambios` a propósito: desde el 15-09-2026 una
   * propuesta pendiente ES un cambio sin guardar, así que ese cálculo las lee.
   */
  const [propuestas, setPropuestas] = useState<MaquinaPropuesta[]>([]);

  /** La corrida en curso, hasta que alguien la acepte o la descarte. */
  const [ejecucionPendiente, setEjecucionPendiente] = useState<number | null>(null);

  /*
   * UNA PROPUESTA PENDIENTE ES UN CAMBIO SIN GUARDAR, y una máquina guardada
   * NO. La distinción es la misma de siempre —cuando el aviso aparecería, la
   * fila ya está en el servidor— pero lo que la IA propuso existe SOLO en el
   * navegador: cambiar de pestaña lo desmonta (Radix no conserva el contenido
   * inactivo) y se perdía sin que nada lo dijera.
   */
  const cambios =
    Number(noAplica !== base.noAplica) +
    Number(descripcion !== base.descripcion) +
    Number(validada !== base.validada) +
    Number(notas !== base.notas) +
    propuestas.length;
  const hayCambios = cambios > 0;

  useEffect(() => {
    onCambios?.(hayCambios);
  }, [hayCambios, onCambios]);

  useEffect(() => () => onCambios?.(false), [onCambios]);

  useImperativeHandle(ref, () => ({
    guardar,
    marcarGuardado: () => {
      setBase({ noAplica, descripcion, validada, notas });

      // EL PANEL SE VACÍA PORQUE LO PROPUESTO YA SE GUARDÓ. Dejarlo en pantalla
      // lo haría ver como pendiente sobre filas que el cuadro ya muestra, y el
      // aviso de cambios sin guardar —que ahora cuenta las propuestas— no se
      // apagaría nunca.
      fuenteDelTexto.olvidar();
      setPropuestas([]);
      setAviso(null);

      // GUARDAR LA PESTAÑA TAMBIÉN ACEPTA LA PROPUESTA, aunque no haya entrado
      // ninguna máquina: lo que se guardó pudo ser la DESCRIPCIÓN que la IA
      // escribió, y eso es tan aceptado como una fila.
      aceptarLaCorrida();
    },
  }));

  /**
   * Anota que la propuesta se usó, una sola vez.
   *
   * Se llama desde los dos caminos que la usan —agregar una máquina y guardar
   * la pestaña— y se olvida la corrida, para que el segundo no vuelva a
   * marcarla.
   */
  function aceptarLaCorrida() {
    if (ejecucionPendiente === null) return;

    resolverElRelleno.mutate({ ejecucionId: ejecucionPendiente, resultado: "aceptado" });
    setEjecucionPendiente(null);
  }

  function pedirElRelleno() {
    setErrorDelRelleno(null);
    setAviso(null);

    rellenar.mutate(undefined, {
      onSuccess: ({ ejecucion_id, propuesta, documentos, pendientes, sin_usar }) => {
        setPropuestas(propuesta.maquinas);
        setEjecucionPendiente(ejecucion_id);

        // LA DESCRIPCIÓN SOLO SE ESCRIBE SI ESTÁ VACÍA, misma regla que en
        // suelos: un texto no se agrega, se pisa — y este lleva casilla
        // "Información validada", así que sobrescribir uno ya revisado dejaría
        // marcado como validado algo que nadie miró.
        //
        // Se lee del ESTADO ACTUAL y no de una copia: leer el expediente tarda
        // varios segundos y quien apretó el botón sigue escribiendo.
        const propuso = propuesta.descripcion_general?.trim() ?? "";
        let escribio = false;

        // LA FUENTE DEL TEXTO SE MUESTRA SOLO MIENTRAS EL TEXTO SIGA SIENDO EL
        // PROPUESTO (el hook lo compara): si el campo ya tenía algo, la IA no
        // escribió y no hay fuente que mostrar. Un relleno nuevo reemplaza la
        // anterior.
        fuenteDelTexto.olvidar();
        if (propuso !== "") {
          fuenteDelTexto.registrar(propuso, propuesta.descripcion_general_fuente ?? null);
        }

        if (propuso !== "") {
          setDescripcion((actual) => {
            if (actual.trim() !== "") return actual;

            escribio = true;

            return propuso;
          });
        }

        setAviso({
          resumen: resumenDeLoPropuestoEnMaquinarias(propuesta.maquinas.length, escribio),
          documentos,
          pendientes,
          sinUsar: sin_usar,
        });
      },

      onError: (e) =>
        setErrorDelRelleno(
          getApiErrorMessage(
            e,
            "No pudimos leer el expediente. Inténtalo de nuevo o carga la maquinaria a mano.",
          ),
        ),
    });
  }


  /**
   * Descarta lo que queda de la propuesta.
   *
   * UN DESCARTE INFORMA TANTO COMO UNA ACEPTACIÓN: sin él, `ia_ejecuciones` solo
   * tendría los casos en que la IA acertó. Las máquinas que YA se agregaron no
   * se tocan — están guardadas y se quitan desde el cuadro.
   */
  function descartarElRelleno() {
    if (ejecucionPendiente !== null) {
      resolverElRelleno.mutate({ ejecucionId: ejecucionPendiente, resultado: "descartado" });
      setEjecucionPendiente(null);
    }

    fuenteDelTexto.olvidar();
    setPropuestas([]);
    setAviso(null);
  }

  /* --- El modal de EDICIÓN --- */

  /** Qué máquina se está corrigiendo, o `null` si el modal está cerrado. */
  const [editando, setEditando] = useState<number | null>(null);
  const enEdicion = editando === null ? null : (maquinas.find((m) => m.id === editando) ?? null);

  /* --- "Rellenar con IA" --- */

  const rellenar = useRellenarMaquinariasConIa(informe.id);
  const resolverElRelleno = useResolverElRelleno();

  const [aviso, setAviso] = useState<AvisoDelRelleno | null>(null);
  const [errorDelRelleno, setErrorDelRelleno] = useState<string | null>(null);

  /**
   * El botón "Guardar máquina" del modal: siempre una corrección.
   *
   * La validación viaja CON los campos: el modal ya decidió si lo editado
   * vuelve a quedar sin validar o si la persona lo volvió a marcar.
   */
  function guardarDesdeElModal(maquina: MaquinariaDeInforme, guardado: GuardadoDeLaMaquina) {
    onCorregir(maquina.id, { ...camposDesde(guardado.formulario), validada: guardado.validada });
    setEditando(null);
  }

  /** El modal ya preguntó: acá solo se quita y se cierra, para no dejarlo sobre una fila que no existe. */
  function quitarDesdeElModal(maquina: MaquinariaDeInforme) {
    onQuitar(maquina.id);
    setEditando(null);
  }

  /**
   * Guarda la pestaña Y LO QUE LA IA PROPUSO.
   *
   * ══ POR QUÉ LO PROPUESTO ENTRA ACÁ ══════════════════════════════════════
   *
   * Hasta el 15-09-2026 cada fila propuesta tenía su botón "Agregar" y había
   * que apretarlos uno por uno. Rodgers miró a alguien usarlo: leyó las filas,
   * bajó al único botón grande de la pantalla —este— y perdió todo lo
   * propuesto. No fue un descuido de esa persona; el panel presenta N filas
   * listas y abajo hay un botón que dice "Guardar", así que nadie espera que
   * ese botón ignore lo de arriba.
   *
   * Lo que se conserva del panel es QUITAR: rechazar una propuesta sigue siendo
   * un acto explícito, solo que ahora es sacarla en vez de no agregarla.
   *
   * ══ SIGUEN SIENDO N+1 PETICIONES, Y ESO NO CAMBIÓ ═══════════════════════
   *
   * Cada máquina se crea con su POST porque de una fila cuelgan FOTOS —es el
   * mismo motivo por el que existe "Guardar máquina"—, así que esto no junta
   * todo en un PUT: dispara un alta por propuesta y después guarda lo de la
   * pestaña. Lo que cambió es QUIÉN aprieta el botón, no cómo viaja.
   *
   * Una propuesta no trae fotos —el documento no las tiene— así que el alta va
   * con la lista vacía, igual que cuando se agregaba de a una.
   */
  function guardar() {
    // LAS QUE YA ESTÁN NO SE VUELVEN A AGREGAR. Se siguen listando en el panel
    // —esconderlas ocultaría algo que el documento sí traía— pero guardarlas
    // otra vez dejaría la máquina duplicada en el informe.
    propuestas
      .filter((maquina) => !yaEstaEnElCuadro(maquina.nombre, maquinas))
      .forEach((maquina) => onAgregar(camposDesde(formularioDesdeLaPropuesta(maquina)), []));

    onGuardar({
      no_aplica: noAplica,
      // Vaciar el textarea BORRA la descripción: `""` viaja como `null`, que
      // es lo que la columna guarda cuando no hay prosa.
      descripcion_general: descripcion.trim() === "" ? null : descripcion,
      descripcion_general_validada: validada,
      notas_observaciones: notas.trim() === "" ? null : notas,
    });
  }

  /** Vuelve la pestaña a lo último guardado y suelta lo que la IA propuso. */
  function descartarLosCambios() {
    setNoAplica(base.noAplica);
    setDescripcion(base.descripcion);
    setValidada(base.validada);
    setNotas(base.notas);
    descartarElRelleno();
  }

  const hayFilas = maquinas.length > 0;

  /** Todo lo que la lista necesita del terreno, o `undefined` con el módulo apagado. */
  const terrenoDeLaLista: TerrenoDeLaLista | undefined = registroDeTerreno
    ? {
        informeId: informe.id,
        modo: modoDelTerreno(terreno),
        encargado: registroDeTerreno.encargado?.nombre ?? null,
        items: seccionDeTerreno?.items ?? [],
        porEnlazar,
        onAbrirFoto: setFotoAbierta,
        // "Es la misma que": un PATCH de la fila elegida. El hook relee el
        // registro y la máquina de terreno deja de listarse suelta.
        onVincular: (uuid, maquinaId) => onCorregir(maquinaId, { terreno_ref: uuid }),
        onQuitar,
        onMantener,
      }
    : undefined;

  return (
    <div className="flex flex-col gap-3.5">
      {noAplica ? null : (
        <BandaDeRellenoConIa
          titulo="Cargar maquinaria desde los documentos"
          rellenando={rellenar.isPending}
          // Con la casilla puesta no hay nada que rellenar: la pestaña ya dijo
          // que la propiedad no tiene maquinaria, y proponerle diez máquinas
          // sería contradecir lo que alguien acaba de declarar.
          disabled={guardando || ocupada || noAplica}
          onRellenar={pedirElRelleno}
        >
          La IA lee los documentos de Antecedentes legales y de Antecedentes históricos y propone
          las máquinas y equipos que encuentre.
          <br />
          Nada se guarda hasta que revises lo propuesto y aprietes «Guardar cambios».
        </BandaDeRellenoConIa>
      )}

      <AvisoDeLoRellenado
        rellenando={rellenar.isPending}
        error={errorDelRelleno}
        aviso={aviso}
        onDescartar={propuestas.length > 0 ? descartarElRelleno : null}
        // LO PROPUESTO SE GUARDA CON LA PESTAÑA, no fila por fila, así que el
        // aviso tiene que mandar al botón de abajo. Decía "cada una se guarda
        // al agregarla" y eso describía el modelo anterior: mandaba a apretar
        // un botón que ya no existe, y contradecía al panel que está justo
        // debajo. Es el mismo defecto que originó el cambio — el texto
        // llevando a la persona al lugar equivocado.
        queHacer={
          "Nada se guardó todavía. Revisa lo propuesto abajo y quita lo que no "
          + "corresponda; las máquinas que queden se guardan al apretar «Guardar "
          + "cambios», al pie de la pestaña."
        }
      />

      {noAplica ? null : (
        /* LAS COLUMNAS SE DECLARAN ACÁ y no dentro del panel: son lo único
           propio de esta pestaña. El panel aporta las decisiones —no esconder
           lo repetido, avisar que agregar guarda— que son iguales en las
           pestañas con cuadro. */
        <FilasPropuestas
          filas={propuestas}
          titulo={(cantidad) =>
            cantidad === 1
              ? "1 máquina propuesta por la IA"
              : `${cantidad} máquinas propuestas por la IA`
          }
          yaEsta={(maquina) => yaEstaEnElCuadro(maquina.nombre, maquinas)}
          fuente={(fila) => fila.fuente}
          ocupada={ocupada}
          onQuitar={(maquina) =>
            setPropuestas((actuales) => actuales.filter((otra) => otra !== maquina))
          }
          columnas={[
            { encabezado: "Nombre/tipo", celda: (m) => m.nombre },
            { encabezado: "Año", celda: (m) => m.anio_fabricacion ?? "—" },
            { encabezado: "Unid.", celda: (m) => m.unidades ?? "—", numerica: true },
            {
              encabezado: "Vida útil T/R",
              celda: (m) => `${m.vida_util_total ?? "—"} / ${m.vida_util_residual ?? "—"}`,
              numerica: true,
            },
            {
              // "UNIT." en el encabezado por lo mismo que en el cuadro de
              // abajo: confundir el valor de un tractor con el de los dos es
              // un error que sale por un factor entero sin verse raro.
              encabezado: "V. unit. reposición",
              celda: (m) => formatearMonto(m.valor_unitario_reposicion, null),
              numerica: true,
            },
            { encabezado: "Moneda", celda: (m) => m.moneda ?? "—" },
          ]}
        />
      )}

      <section className="bg-card flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-4 py-3">
        <label
          title={hayFilas ? "Quita primero las máquinas de la lista" : undefined}
          className={cn(
            "flex w-fit items-center gap-2.5",
            hayFilas && !noAplica ? "cursor-not-allowed opacity-55" : "cursor-pointer",
          )}
        >
          <Checkbox
            checked={noAplica}
            // Con máquinas cargadas la fila se contradiría sola, y el servidor la
            // rechaza con un 422. Se apaga acá para que el rechazo no sea la
            // forma de enterarse.
            disabled={guardando || hayFilas}
            onCheckedChange={(v) => setNoAplica(v === true)}
          />
          <span className="text-[0.8125rem] font-bold">
            La propiedad no cuenta con maquinaria ni equipos
          </span>
        </label>
        {noAplica || hayFilas ? (
          <span className="text-ink-muted text-[0.71875rem] font-semibold">
            {noAplica
              ? "La sección se imprimirá indicando que no hay maquinaria."
              : "Para marcarlo, quita antes las máquinas de la lista."}
          </span>
        ) : null}
      </section>

      {porEnlazar.length > 0 && !noAplica ? (
        <section
          aria-label="Máquinas y equipos creados en terreno"
          className="bg-info-soft/50 border-info-dot/30 flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-4 py-3"
        >
          <span aria-hidden="true" className="bg-info-dot size-2 flex-none rounded-full" />
          <div className="min-w-0 flex-1">
            <h3 className="text-ink text-[0.8125rem] font-bold">{tituloDelAviso(porEnlazar.length)}</h3>
            <p className="text-ink-body mt-0.5 text-[0.71875rem] font-semibold text-pretty">
              Llegan desde la App solo con nombre, año y unidades. Completa la valorización a partir
              de sus fotos y audios, o indica si es la misma que una ya cargada desde documentos.
            </p>
          </div>
        </section>
      ) : null}

      {noAplica ? null : (
        <ListaDeMaquinarias
          maquinas={maquinas}
          ocupada={ocupada}
          onAgregar={onAgregar}
          onAbrir={(maquina) => setEditando(maquina.id)}
          // SE GUARDA SOLA y viaja sin el resto de los campos, para no pisar
          // lo que otro pudo haber corregido mientras tanto.
          onValidar={(maquina, validadaAhora) => onCorregir(maquina.id, { validada: validadaAhora })}
          terreno={terrenoDeLaLista}
        />
      )}

      <DescripcionDeLaMaquinaria
        informeId={informe.id}
        // Sin lista la descripción abre la pestaña: la numeración no tiene huecos.
        numero={noAplica ? "01" : "02"}
        descripcion={descripcion}
        onDescripcion={setDescripcion}
        validada={validada}
        onValidada={setValidada}
        disabled={guardando}
        fuente={
          fuenteDelTexto.visible ? <FuenteDeLaPropuesta fuente={fuenteDelTexto.visible.fuente} /> : null
        }
        terreno={
          <TerrenoDeLaDescripcion
            informeId={informe.id}
            terreno={terreno}
            campo={campoDeLaDescripcionDeMaquinarias(registroDeTerreno)}
            campoRedactable="maquinarias"
            valorActual={descripcion}
            asignado={asignado}
            onAbrirFoto={setFotoAbierta}
            onRedactado={(texto) => {
              // Lo escribió un modelo: nadie lo revisó todavía, y la fuente que
              // declaró el relleno anterior ya no es la de este texto.
              fuenteDelTexto.olvidar();
              setDescripcion(texto);
              setValidada(false);
            }}
          />
        }
      />

      <section
        aria-labelledby="maquinarias-notas-titulo"
        className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5"
      >
        <EncabezadoNumerado
          numero={noAplica ? "02" : "03"}
          idDelTitulo="maquinarias-notas-titulo"
          titulo="Notas u observaciones"
          bajada="Opcional."
        />
        <div className="mt-3.5">
          <NotasDeLaPestana
            id="notas-maquinaria"
            value={notas}
            onChange={setNotas}
            disabled={guardando}
            plana
          />
        </div>
      </section>

      {/* LA BARRA VA AL PIE de la pestaña. Cuenta lo que es de la PESTAÑA
          (casilla, descripción, validación, notas y cada propuesta de la IA);
          las máquinas no entran porque ya están guardadas. */}
      <BarraDeGuardado
        cambios={cambios}
        enviando={guardando}
        etiquetaEnvio="Guardar cambios"
        ayuda="Las máquinas y sus fotos se guardan solas al agregarlas o editarlas."
        onGuardar={guardar}
        onDescartar={descartarLosCambios}
      />

      <DialogoDeMaquina
        maquina={enEdicion}
        informeId={informe.id}
        ocupada={ocupada}
        onGuardar={guardarDesdeElModal}
        onQuitar={quitarDesdeElModal}
        onCerrar={() => setEditando(null)}
        onSubirFoto={onSubirFoto}
        onQuitarFoto={onQuitarFoto}
        onLeyendaDeFoto={onLeyendaDeFoto}
      />

      {registroDeTerreno ? (
        <VisorDeFotos
          informeId={informe.id}
          fotos={fotosDelRegistro}
          abierta={fotoAbierta}
          onCambiar={setFotoAbierta}
        />
      ) : null}
    </div>
  );
}
